<template>
  <div class="session-view">
    <SessionSearch 
      :model-value="props.searchQuery"
      :result-count="searchResultCount"
      :loading="props.searchLoading"
      @search="handleSearch"
      ref="sessionSearchRef"
    />
    <div v-if="showNowListeningDuringSearch && filteredNowListeningSession" class="now-listening-block">
      <div class="now-listening-header clickable-session" @click="openNowListeningDetail()">
        <div class="now-listening-info">
          <EnhancedTierBadge 
            tier="Now Listening To" 
            variant="medal" 
            size="large"
            :glow-effect="true"
          />
        </div>
        <div class="session-action-hint">
          <button class="session-expand-btn" :aria-label="`Expand now listening details`" title="Expand now listening details">
            <Expand :size="18" class="expand-icon" />
          </button>
        </div>
      </div>
      <div class="albums-scroll-wrapper">
        <div class="albums-scroll-container">
          <albumCover
            v-for="album in sortedAlbums(filteredNowListeningSession.albums)"
            :key="album.id"
            :album="album"
            :showTierBorder="true"
            :class="tierClass(album.tier)"
            @albumClicked="() => handleAlbumClicked(album)"
          />
        </div>
      </div>
    </div>
    <LoadingSpinner v-if="loading" size="large" message="Loading sessions..." />
    
    <!-- No Results Display -->
    <div v-else-if="props.searchQuery && filteredSessions.length === 0 && !showNowListeningDuringSearch" class="no-results">
      <div class="no-results-icon">🔍</div>
      <h3>No sessions found</h3>
      <p>No albums match your search for "<strong>{{ props.searchQuery }}</strong>". Try a different search term.</p>
      <button class="clear-search-btn" @click="clearSearchLocal">Clear Search</button>
    </div>
    
    <div v-else>
      <div v-for="session in filteredSessions" :key="session.id">
        <div v-if="session.albums && session.albums.length > 0" class="session-block">
          <div class="session-header clickable-session" @click="openSessionDetail(session.id)">
            <div class="session-info">
              <span class="session-date">📅 {{ formatDate(session.session_date) }}</span>
              <span class="session-location">📍 {{ session.location }}</span>
            </div>
            <div class="session-action-hint">
              <button class="session-expand-btn" :aria-label="`Expand session details`" title="Expand session details">
                <Expand :size="18" class="expand-icon" />
              </button>
            </div>
          </div>
          <div class="albums-scroll-wrapper">
            <div class="albums-scroll-container">
              <albumCover
                v-for="album in sortedAlbums(session.albums)"
                :key="album.id"
                :album="album"
                :showTierBorder="true"
                :class="tierClass(album.tier)"
                @albumClicked="() => handleAlbumClicked(album)"
              />
            </div>
          </div>
        </div>
      </div>
      
      <!-- Infinite scroll loading states -->
      <div v-if="loadingMore" class="loading-more-sessions">
        <LoadingSpinner size="medium" message="Loading more sessions..." />
      </div>
      
      <!-- End of list indicator -->
      <div v-else-if="!hasNext && sessions.length > 0" class="end-of-sessions">
        <div class="end-indicator">
          <div class="end-icon">✅</div>
          <p>You've reached the end of all sessions</p>
          <span class="session-count">{{ sessions.length }} session{{ sessions.length === 1 ? '' : 's' }} total</span>
        </div>
      </div>
      
      <!-- Error state for pagination -->
      <div v-if="error && sessions.length > 0" class="pagination-error">
        <div class="error-message">
          <span class="error-icon">⚠️</span>
          <span>{{ error }}</span>
          <button @click="emit('retry')" class="retry-btn">Try Again</button>
        </div>
      </div>
    </div>
    <AlbumDetailsModal v-if="selectedAlbum" :album="selectedAlbum" @close="handleAlbumModalClose" />
    
    <!-- Session Detail Modal -->
    <SessionDetailModal 
      v-if="showSessionModal && selectedSessionId"
      :show="showSessionModal"
      :session-id="selectedSessionId"
      :now-listening-session="filteredNowListeningSession"
      @close="closeSessionDetail"
      @albumClicked="handleAlbumClickedFromModal"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import albumCover from './albumCover.vue';
import LoadingSpinner from './LoadingSpinner.vue';
import AlbumDetailsModal from './modals/AlbumDetailsModal.vue';
import EnhancedTierBadge from './EnhancedTierBadge.vue';
import SessionSearch from './search/SessionSearch.vue';
import SessionDetailModal from './modals/SessionDetailModal.vue';
import { Expand } from 'lucide-vue-next';

const TIER_ORDER = ["S", "A", "B", "C", "D", "E", "F"];

// Emits
const emit = defineEmits(['retry']);

// Props
const props = defineProps({
  sessions: { type: Array, required: true },
  loading: { type: Boolean, required: true },
  selectedAlbum: { type: Object, default: null },
  handleAlbumClicked: { type: Function, required: true },
  nowListeningVisible: { type: Boolean, default: false },
  nowListeningSession: { type: Object, default: null },
  // Infinite scroll props
  hasNext: { type: Boolean, default: false },
  loadingMore: { type: Boolean, default: false },
  error: { type: String, default: null },
  // Search props
  searchQuery: { type: String, default: '' },
  searchMode: { type: String, default: 'auto' },
  isSearchActive: { type: Boolean, default: false },
  searchLoading: { type: Boolean, default: false },
  performSearch: { type: Function, required: true },
  clearSearch: { type: Function, required: true }
})

// Local refs for component
const sessionSearchRef = ref(null)

// Session detail modal state
const showSessionModal = ref(false)
const selectedSessionId = ref(null)
const isAlbumOpenedFromSession = ref(false)

// Use sessions from props directly (search is handled at API level now)
const filteredSessions = computed(() => {
  return props.sessions
})

// Use Now Listening session from props directly (search filtering is done at API level)
const filteredNowListeningSession = computed(() => {
  return props.nowListeningSession
})

// Computed - Check if Now Listening should be visible during search
const showNowListeningDuringSearch = computed(() => {
  return props.nowListeningVisible && 
         filteredNowListeningSession.value && 
         filteredNowListeningSession.value.albums && 
         filteredNowListeningSession.value.albums.length > 0
})

// Computed - Count total matching albums across all sessions (including Now Listening)
const searchResultCount = computed(() => {
  if (!props.searchQuery) return 0
  
  let total = filteredSessions.value.reduce((sum, session) => {
    return sum + (session.albums ? session.albums.length : 0)
  }, 0)
  
  // Add Now Listening albums to count
  if (filteredNowListeningSession.value && filteredNowListeningSession.value.albums) {
    total += filteredNowListeningSession.value.albums.length
  }
  
  return total
})

// Methods
const formatDate = (dateStr) => {
  // Use only the date part if present (YYYY-MM-DD)
  const match = typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}/);
  const dateOnly = match ? match[0] : dateStr;
  const d = new Date(dateOnly);
  return d.toLocaleDateString();
}

const sortedAlbums = (albums) => {
  // Sort by tier order, fallback to end if not found
  return [...albums].sort((a, b) => {
    const aIdx = TIER_ORDER.indexOf(a.tier);
    const bIdx = TIER_ORDER.indexOf(b.tier);
    return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
  });
}

const tierClass = (tier) => {
  return `tier-${tier ? tier.toLowerCase() : 'unknown'}`;
}

const handleSearch = (query) => {
  props.performSearch(query, 'auto')
}

const clearSearchLocal = () => {
  props.clearSearch()
  if (sessionSearchRef.value) {
    sessionSearchRef.value.clearSearch()
  }
}

// Session detail modal methods
const openSessionDetail = (sessionId) => {
  selectedSessionId.value = sessionId
  showSessionModal.value = true
}

const closeSessionDetail = () => {
  showSessionModal.value = false
  selectedSessionId.value = null
  isAlbumOpenedFromSession.value = false  // Reset flag when session modal closes
}

const handleAlbumClickedFromModal = (album) => {
  // Set flag that album was opened from session
  isAlbumOpenedFromSession.value = true
  // Close session modal first
  showSessionModal.value = false
  // Then open album modal
  props.handleAlbumClicked(album)
}

const handleAlbumModalClose = () => {
  // Close the album modal first
  props.handleAlbumClicked(null)
  
  // If album was opened from session, reopen the session modal
  if (isAlbumOpenedFromSession.value) {
    isAlbumOpenedFromSession.value = false
    showSessionModal.value = true
  }
}

const openNowListeningDetail = () => {
  // Use a special session ID to indicate "Now Listening"
  selectedSessionId.value = 'now-listening'
  showSessionModal.value = true
}
</script>

<style scoped>
.session-view {
  padding: 0;
}
.session-block {
  background: #232323;
  border-radius: 8px;
  margin-bottom: 24px;
  padding: 0; /* Remove padding to align with Now Listening To */
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
}
.session-header {
  display: flex;
  justify-content: space-between;
  font-size: 1.1em;
  margin-bottom: 8px;
  color: #b3e5fc;
  padding: 16px 16px 0 16px; /* Add padding to header only */
}

.clickable-session {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  margin: 0 0 8px 0;  /* Removed negative margins to prevent overflow */
  padding: 16px 16px 12px 16px;  /* Adjusted padding to match container */
  align-items: center;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.clickable-session:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(179, 229, 252, 0.3);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.session-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.session-action-hint {
  display: flex;
  align-items: center;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.clickable-session:hover .session-action-hint {
  opacity: 1;
}

.session-expand-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(8px);
  flex-shrink: 0;
}

.session-expand-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
  color: #ffffff;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
}

.session-expand-btn:active {
  transform: scale(0.95);
}

.session-expand-btn .expand-icon {
  transition: transform 0.2s ease;
  stroke: currentColor;
  fill: none;
  width: 18px !important;
  height: 18px !important;
  min-width: 18px;
  min-height: 18px;
  display: block;
  stroke-width: 2;
}

.session-expand-btn:hover .expand-icon {
  transform: scale(1.1);
}
.albums-scroll-wrapper {
  overflow-x: auto;
  overflow-y: visible; /* Allow vertical overflow for hover effects */
  padding: 0; /* Match Now Listening To padding */
}
.albums-scroll-container {
  display: flex;
  gap: 8px; /* Match Now Listening To gap */
  padding: 16px 12px 20px 12px; /* Match Now Listening To padding */
  overflow-y: visible; /* Ensure vertical overflow is visible */
}
@media (max-width: 600px) {
  .session-block { padding: 0; } /* Keep no padding on mobile */
  .session-header { padding: 8px 8px 0 8px; } /* Add mobile padding to header only */
  
  /* Mobile clickable session styles */
  .clickable-session {
    margin: 0 0 8px 0;  /* Removed negative margins for mobile too */
    padding: 12px 8px 8px 8px;  /* Adjusted padding to match container */
    font-size: 0.9em; /* Smaller text on mobile */
  }
  
  .session-info {
    gap: 2px;
  }
  
  .session-date,
  .session-location {
    font-size: 0.85em; /* Even smaller date/location text */
  }
  
  .session-expand-btn {
    width: 40px;
    height: 40px;
  }
  
  .session-expand-btn .expand-icon {
    width: 18px !important;
    height: 18px !important;
  }
  
  .albums-scroll-container { 
    gap: 8px; /* Match Now Listening To gap on mobile */
    padding: 16px 8px 20px 8px; /* Keep mobile padding */
  }
  .albums-scroll-wrapper {
    padding: 0; /* Match Now Listening To wrapper padding */
  }
  .now-listening-block {
    padding: 0;  /* Completely removed all padding */
    margin-bottom: 16px;
    border-radius: 6px;
  }
  .now-listening-header {
    font-size: 1.05em;
    margin-bottom: 6px;
    padding: 8px 16px;  /* Reduced padding specifically for mobile */
  }
}
.now-listening-block {
  background: #232323; /* Black background like tierlist view */
  border-radius: 16px;
  margin-bottom: 24px;
  overflow: hidden;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideInFromBottom 0.6s ease;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}
.now-listening-header {
  font-size: 18px;
  font-weight: 700;
  display: flex;
  padding: 12px 20px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
  background: rgb(58, 39, 98, 0.2); /* Now listening - subtle purple */
  backdrop-filter: blur(8px);
  border-radius: 12px 12px 0 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.5px;
  color: #ffffff;
  margin-bottom: 0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.now-listening-header:hover {
  background: rgba(58, 39, 98, 0.3);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.now-listening-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.now-listening-text {
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1.2px;
}

/* Now Listening albums scroll styling to match tierlist view */
.now-listening-block .albums-scroll-wrapper {
  position: relative;
  overflow: visible; /* Allow all overflow for hover effects */
  margin-bottom: 0; /* Remove bottom margin to eliminate light bar */
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(12px);
  padding: 0; /* No padding for maximum space efficiency */
}

.now-listening-block .albums-scroll-container {
  display: flex;
  overflow-x: auto;
  overflow-y: visible; /* Ensure vertical overflow is visible */
  gap: 8px; /* Further reduced gap for space efficiency */
  padding: 16px 12px 20px 12px; /* Optimized padding - enough for hover effects */
  scrollbar-width: thin;
  align-items: flex-start;
}


/* No Results Display */
.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: rgba(255, 255, 255, 0.02);
  border: 2px dashed rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  margin: 24px 0;
  text-align: center;
  animation: fadeInUp 0.5s ease;
}

.no-results-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.no-results h3 {
  font-size: 20px;
  font-weight: 600;
  color: #f3f3f3;
  margin: 0 0 8px 0;
}

.no-results p {
  font-size: 14px;
  color: rgba(243, 243, 243, 0.7);
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.clear-search-btn {
  padding: 12px 24px;
  background: linear-gradient(135deg, rgba(45, 140, 240, 0.9) 0%, rgba(45, 140, 240, 0.7) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 44px;
}

.clear-search-btn:hover {
  background: linear-gradient(135deg, rgba(45, 140, 240, 1) 0%, rgba(45, 140, 240, 0.8) 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.4);
}

/* Enhanced Transitions */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Infinite Scroll Loading States */
.loading-more-sessions {
  display: flex;
  justify-content: center;
  padding: 24px;
  margin: 16px 0;
}

.end-of-sessions {
  display: flex;
  justify-content: center;
  padding: 32px 24px;
  margin: 16px 0;
}

.end-indicator {
  text-align: center;
  background: rgba(255, 255, 255, 0.02);
  border: 2px dashed rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 24px;
  max-width: 300px;
}

.end-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.end-indicator p {
  font-size: 16px;
  font-weight: 500;
  color: #f3f3f3;
  margin: 0 0 8px 0;
}

.session-count {
  font-size: 14px;
  color: rgba(243, 243, 243, 0.7);
}

.pagination-error {
  display: flex;
  justify-content: center;
  padding: 16px;
  margin: 16px 0;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  color: #ef4444;
}

.error-icon {
  font-size: 20px;
}

.retry-btn {
  padding: 8px 16px;
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 6px;
  color: #ef4444;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-btn:hover {
  background: rgba(239, 68, 68, 0.3);
  border-color: rgba(239, 68, 68, 0.6);
}
</style> 